import {
  Infer,
  boolean,
  enums,
  nullable,
  object,
  string,
  union,
} from "superstruct";
import { ModCfFileImage, ModCfProportionsValue } from "types/cf-utils/types";
import { nullUndefined } from "utilities/validatoros/types";

export const ModCfBgColors = enums([
  "",
  "white",
  "black",
  "primary",
  "secondary",
  "whiteTransparent",
  "blackTransparent",
  "gradientTpay",
  "gradientTpayV2",
  "gradientTpayVertical",
  "gradientTpayTopLeft",
  "gradientTpayTopRight",
  "gradientTpayBottomLeft",
  "gradientTpayBottomRight",
  "gradientBrandBlue",
  "accent",
  "neutralPrimary",
  "neutralSecondary",
  "neutralTertiary",
  "neutralQuaternary",
  "brandPrimary",
  "brandSecondary",
  "success",
  "warning",
  "error",
]);

export const ModCfBgPositions = enums([
  "initial",
  "left top",
  "left center",
  "left bottom",
  "right top",
  "right center",
  "right bottom",
  "center top",
  "center center",
  "center bottom",
]);

export const ModSizeSpacers = enums([
  "xxs",
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "xxl",
  "none",
]);

export const ModCfStepTextAlign = enums(["left", "center", "right"]);

export const ModCfJustifyContent = enums(["flex-start", "center", "flex-end"]);

export const ModCfAlignItems = enums(["flex-start", "center", "flex-end"]);

export const ModCfBgSize = enums(["auto", "cover", "contain"]);

export const ModCfSpacers = object({
  paddingTop: nullUndefined(ModSizeSpacers),
  paddingBottom: nullUndefined(ModSizeSpacers),
  marginTop: nullUndefined(ModSizeSpacers),
  marginBottom: nullUndefined(ModSizeSpacers),
});

export const ModCfBackground = object({
  __typename: enums(["ContentfulBackgroundV2"]),
  id: string(),
  contentful_id: string(),
  alt: nullable(string()),
  slug: nullable(string()),
  customClass: nullable(string()),
  backgroundColor: nullUndefined(ModCfBgColors),
  backgroundImage: nullUndefined(ModCfFileImage),
  backgroundImageMobile: nullUndefined(ModCfFileImage),
  backgroundPosition: nullUndefined(ModCfBgPositions),
  backgroundSize: nullUndefined(ModCfBgSize),
});

export const ModCfStyleGeneric = object({
  background: nullUndefined(ModCfBackground),
  spacers: nullUndefined(ModCfSpacers),
  justifyContent: nullUndefined(ModCfJustifyContent),
  alignItems: nullUndefined(ModCfAlignItems),
});

export const ModCfMediaQueriesTextAlign = object({
  contentful_id: string(),
  default: union([ModCfStepTextAlign, enums(["justify"])]),
  sm: nullable(ModCfStepTextAlign),
  md: nullable(ModCfStepTextAlign),
  lg: nullable(ModCfStepTextAlign),
  xl: nullable(ModCfStepTextAlign),
});

export const ModStepCol = enums(["1", "2", "3", "4", "5", "6", "7", "8"]);

export const ModCfMediaQueriesConfigColumn = object({
  default: ModStepCol,
  sm: nullable(ModStepCol),
  md: nullable(ModStepCol),
  lg: nullable(ModStepCol),
  xl: nullable(ModStepCol),
});

export const ModStepWrapper = enums(["sm", "md", "lg", "fluid"]);

export const ModMediaQueriesConfigBool = object({
  default: boolean(),
  sm: nullUndefined(boolean()),
  md: nullUndefined(boolean()),
  lg: nullUndefined(boolean()),
  xl: nullUndefined(boolean()),
});

export const ModCfMediaQueriesConfigWrapper = object({
  default: ModStepWrapper,
  md: nullable(ModStepWrapper),
  lg: nullable(ModStepWrapper),
  xl: nullable(ModStepWrapper),
});

export type CfBgColors = Infer<typeof ModCfBgColors>;

export type BgColors =
  | ""
  | "white"
  | "accent"
  | "primary"
  | "secondary"
  | "neutralPrimary"
  | "neutralSecondary"
  | "neutralTertiary"
  | "neutralQuaternary"
  | "brandPrimary"
  | "brandSecondary"
  | "white"
  | "black"
  | "whiteTransparent"
  | "blackTransparent"
  | "success"
  | "warning"
  | "error"
  | "gradientBrandBlue"
  | "gradientTpay"
  | "gradientTpayV2"
  | "gradientTpayVertical"
  | "gradientTpayTopLeft"
  | "gradientTpayTopRight"
  | "gradientTpayBottomLeft"
  | "gradientTpayBottomRight";

export const ModCfColors = enums([
  "",
  "white",
  "black",
  "accent",
  "primary",
  "secondary",
  "neutralPrimary",
  "neutralSecondary",
  "neutralTertiary",
  "neutralQuaternary",
  "brandPrimary",
  "brandSecondary",
  "success",
  "warning",
  "error",
]);

export type CfColors = Infer<typeof ModCfColors>;

export type Colors =
  | ""
  | "white"
  | "black"
  | "accent"
  | "primary"
  | "secondary"
  | "neutralPrimary"
  | "neutralSecondary"
  | "neutralTertiary"
  | "neutralQuaternary"
  | "brandPrimary"
  | "brandSecondary"
  | "success"
  | "warning"
  | "error"
  | "gradientTpay";

export type CfBgPositions = Infer<typeof ModCfBgPositions>;

// NOTE: to remove, this is for old boostrap design
export type BgPositions =
  | ""
  | "initial"
  | "lt"
  | "lc"
  | "lb"
  | "rt"
  | "rc"
  | "rb"
  | "ct"
  | "cc"
  | "cb";

export type CfBgSize = Infer<typeof ModCfBgSize>;

// NOTE: to remove, this is for old boostrap design
export type BgSize = "" | "auto" | "cover" | "contain";

export type CfBackground = Infer<typeof ModCfBackground>;

export interface Background {
  backgroundColor?: BgColors;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  backgroundPosition?: BgPositions;
  backgroundSize?: BgSize;
}

export type BackgroundColorProperties = Omit<
  Background,
  "backgroundImage" | "backgroundImageMobile"
>;

export type MediaQueriesConfigColumn = Infer<
  typeof ModCfMediaQueriesConfigColumn
>;

export type BreakpointValue = "sm" | "md" | "lg" | "xl";

export type StepWrapper = Infer<typeof ModStepWrapper>;

export type CfMediaQueriesConfigWrapper = Infer<
  typeof ModCfMediaQueriesConfigWrapper
>;

type StepTextAlign = "left" | "center" | "right";

export interface TextAlign {
  defaultTextAlign?: StepTextAlign | "justify";
  smTextAlign?: StepTextAlign;
  mdTextAlign?: StepTextAlign;
  lgTextAlign?: StepTextAlign;
  xlTextAlign?: StepTextAlign;
}

export type SizeSpacers = Infer<typeof ModSizeSpacers>;

export type CfSpacers = Infer<typeof ModCfSpacers>;
export interface Spacers {
  paddingTop?: SizeSpacers | "none";
  paddingBottom?: SizeSpacers | "none";
  marginTop?: SizeSpacers | "none";
  marginBottom?: SizeSpacers | "none";
}

export type CfJustifyContent = Infer<typeof ModCfJustifyContent>;
export type JustifyContent = "start" | "end" | "center" | "between" | "around";

export type CfAlignItems = "flex-start" | "center" | "flex-end";
export type AlignItems = "start" | "end" | "center" | "baseline" | "stretch";

// CF

export interface WrapContent {
  xsWrapContent?: StepWrapper;
  mdWrapContent?: StepWrapper;
  lgWrapContent?: StepWrapper;
  xlWrapContent?: StepWrapper;
}

export type ColOrderValue =
  | ""
  | "first"
  | "last"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12";

export type CfQueryOrder = Infer<typeof ModMediaQueriesConfigBool>;

export interface ColOrder {
  xsColOrder?: ColOrderValue;
  smColOrder?: ColOrderValue;
  mdColOrder?: ColOrderValue;
  lgColOrder?: ColOrderValue;
  xlColOrder?: ColOrderValue;
}

type CfColSizeValue = "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8";
export type ColSizeValue = "12" | "6" | "4" | "3" | "2" | "1";

export interface CfColSize {
  default?: CfColSizeValue;
  sm?: CfColSizeValue;
  md?: CfColSizeValue;
  lg?: CfColSizeValue;
  xl?: CfColSizeValue;
}
export interface ColSize {
  xsColSize: ColSizeValue;
  smColSize?: ColSizeValue;
  mdColSize?: ColSizeValue;
  lgColSize?: ColSizeValue;
  xlColSize?: ColSizeValue;
}

type CfProportionsValue = Infer<typeof ModCfProportionsValue>;

export type ProportionsCol =
  | ["6", "6"]
  | ["5", "7"]
  | ["7", "5"]
  | ["4", "8"]
  | ["8", "4"]
  | ["3", "9"]
  | ["9", "3"]
  | ["2", "10"]
  | ["10", "2"]
  | ["1", "11"]
  | ["11", "1"];

export type Proportions = {
  [k in CfProportionsValue]: ProportionsCol;
};

export type CfPropTypeDictionary<CfT extends string | number | symbol, T> = {
  [k in CfT]?: T;
};
